import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';
import user from 'js/lib/user';

import { useFreeTrialRemovalTreatment } from 'bundles/enroll-common/utils/freeTrialRemovalExperimentUtils';
import S12nBulkPayEnrollModal from 'bundles/enroll-modal/components/bulk-pay/S12nBulkPayEnrollModal';
import CourseraPlusEnrollModal from 'bundles/enroll-modal/components/coursera-plus/CourseraPlusEnrollModal';
import S12nNonRecurringEnrollModal from 'bundles/enroll-modal/components/s12n-non-recurring/S12nNonRecurringEnrollModal';
import S12nPaymentMethodEnrollModal from 'bundles/enroll-modal/components/s12n-non-recurring/S12nPaymentMethodEnrollModal';
import S12nPreEnrollModal from 'bundles/enroll-modal/components/s12n-pre-enrollment/S12nPreEnrollModal';
import S12nSubscriptionEnrollModal from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollModal';
import S12nSubscriptionEnrollModalNoFreeTrial from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollModalNoFreeTrial';
import UserInterestModal from 'bundles/enroll-modal/components/user-interest/UserInterestModal';
import { useS12nEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import EnterpriseEnrollmentChoiceModalForXDP from 'bundles/enterprise-legacy-learner-home/components/EnterpriseEnrollmentChoiceModalForXDP';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import paymentsExperiments from 'bundles/epic/clients/__generated__/payments';

export type PropsFromCaller = {
  s12nId: string;
  courseId?: string;
  partnerIds?: Array<string>;
  onSdp?: boolean;
  onClose: () => void;
};

const S12nEnrollModal = ({ s12nId, onClose, courseId, onSdp = false }: PropsFromCaller) => {
  const {
    enrollmentAvailableChoices,
    course,
    programs,
    invitedPrograms,
    thirdPartyOrganizations,
    invitedThirdPartyOrganizations,
  } = useS12nEnrollModalData();

  const {
    canSubscribeToS12n,
    canEnrollThroughCourseraPlus,
    canEnrollThroughS12nPrepaid,
    canEnrollThroughProgram,
    canEnrollThroughProgramInvitation,
    canPurchaseThroughCourseraPlus,
  } = enrollmentAvailableChoices;

  const canSeePaymentMethodModal = canEnrollThroughS12nPrepaid && canSubscribeToS12n;
  const isCourseraPlusPrepaidEnabled = requestCountry.get() === 'IN';
  const canSeeNonRecurringModal =
    (canEnrollThroughS12nPrepaid && !canSubscribeToS12n) ||
    (isCourseraPlusPrepaidEnabled && canPurchaseThroughCourseraPlus);

  const isEnterpriseUser = canEnrollThroughProgram || canEnrollThroughProgramInvitation;
  const waitForBothProgramDataFetch = canEnrollThroughProgram && canEnrollThroughProgramInvitation;
  const isFreeTrialRemovalTreatment = useFreeTrialRemovalTreatment(canSubscribeToS12n);

  const shouldShowPreEnrollModal =
    !paymentsExperiments.preview('disablePreEnrollment') &&
    (enrollmentAvailableChoices.canPreEnroll || enrollmentAvailableChoices.isPreEnrolled);

  // Enterprise learners must not enroll in a pre-enrollable s12n that isn't available yet, so only collect their interest.
  const shouldShowUserInterestModal = enrollmentAvailableChoices.canPreEnroll && canEnrollThroughProgram;

  // For the list of s12ns that are in the pre enrolled flow, show the pre enroll modal before any other s12n modal
  if (shouldShowUserInterestModal) {
    return <UserInterestModal courseId={courseId ?? course.id} onClose={onClose} />;
  } else if (shouldShowPreEnrollModal && s12nId) {
    return (
      <S12nPreEnrollModal
        s12nId={s12nId}
        onSdp={onSdp}
        onClose={onClose}
        courseId={courseId}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
  } else if (isEnterpriseUser) {
    return (
      <EnterpriseEnrollmentChoiceModalForXDP
        thirdPartyOrganizations={thirdPartyOrganizations}
        programs={programs}
        invitedPrograms={invitedPrograms}
        invitedThirdPartyOrganizations={invitedThirdPartyOrganizations}
        productId={s12nId}
        productType={PRODUCT_TYPES.SPECIALIZATION}
        courseSlug={course.slug}
        userId={user.get().id}
        handleClose={onClose}
        waitForBothProgramDataFetch={waitForBothProgramDataFetch}
      />
    );
    // Only trigger Coursera Plus flow for users who are already subscribed users
  } else if (canEnrollThroughCourseraPlus) {
    return <CourseraPlusEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} />;
  } else if (canSeePaymentMethodModal) {
    return <S12nPaymentMethodEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} onSdp={onSdp} />;
  } else if (canSeeNonRecurringModal) {
    return <S12nNonRecurringEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} onSdp={onSdp} />;
  } else if (canSubscribeToS12n) {
    if (isFreeTrialRemovalTreatment) {
      return <S12nSubscriptionEnrollModalNoFreeTrial onClose={onClose} onSdp={onSdp} />;
    }
    return <S12nSubscriptionEnrollModal onClose={onClose} onSdp={onSdp} />;
  } else {
    return <S12nBulkPayEnrollModal onClose={onClose} onSdp={onSdp} />;
  }
};

export default S12nEnrollModal;
